import React, { useState, useEffect } from 'react';
import API from '../../api/axios';
import { formatDate } from '../../utils/dateUtils';
import { FiBell, FiCheck, FiCheckCircle, FiFileText, FiCreditCard, FiAlertTriangle } from 'react-icons/fi';

const NotificationsPage = () => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);

  const fetchNotifications = async () => {
    try {
      const res = await API.get('/notifications');
      setNotifications(res.data.data.notifications);
      setUnreadCount(res.data.data.unreadCount);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNotifications();
  }, []);

  const markAsRead = async (id) => {
    try {
      await API.put(`/notifications/${id}/read`);
      setNotifications((prev) =>
        prev.map((n) => (n.id === id ? { ...n, is_read: true } : n))
      );
      setUnreadCount((c) => Math.max(0, c - 1));
    } catch (err) {
      console.error(err);
    }
  };

  const markAllRead = async () => {
    try {
      await API.put('/notifications/read-all');
      setNotifications((prev) => prev.map((n) => ({ ...n, is_read: true })));
      setUnreadCount(0);
    } catch (err) {
      console.error(err);
    }
  };

  const getIcon = (title) => {
    if (title?.includes('facture')) return <FiFileText />;
    if (title?.includes('Paiement confirmé')) return <FiCreditCard />;
    if (title?.includes('échoué')) return <FiAlertTriangle />;
    return <FiBell />;
  };

  const getIconColor = (title) => {
    if (title?.includes('confirmé')) return 'var(--success)';
    if (title?.includes('échoué')) return 'var(--danger)';
    return 'var(--primary)';
  };

  if (loading) {
    return <div className="loading-page"><div className="spinner"></div><p>Chargement...</p></div>;
  }

  return (
    <div>
      <div className="page-header">
        <div>
          <h2 style={{ fontSize: 22, fontWeight: 600 }}>
            Notifications {unreadCount > 0 && <span className="badge badge-primary" style={{ background: 'var(--primary)', color: 'white', marginLeft: 8, fontSize: 13 }}>{unreadCount}</span>}
          </h2>
          <p style={{ color: 'var(--gray-500)', fontSize: 14 }}>Vos alertes et mises à jour</p>
        </div>
        {unreadCount > 0 && (
          <button className="btn btn-outline" onClick={markAllRead}>
            <FiCheckCircle /> Tout marquer comme lu
          </button>
        )}
      </div>

      {notifications.length === 0 ? (
        <div className="card">
          <div className="empty-state">
            <FiBell style={{ width: 48, height: 48, opacity: 0.3 }} />
            <h3>Aucune notification</h3>
            <p>Vous recevrez des notifications lorsqu'une facture sera créée ou un paiement traité.</p>
          </div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {notifications.map((n) => (
            <div
              key={n.id}
              className="card"
              style={{
                padding: '16px 20px',
                display: 'flex',
                alignItems: 'flex-start',
                gap: 16,
                opacity: n.is_read ? 0.7 : 1,
                borderLeft: n.is_read ? 'none' : `4px solid var(--primary)`,
                cursor: n.is_read ? 'default' : 'pointer',
              }}
              onClick={() => !n.is_read && markAsRead(n.id)}
            >
              <div style={{
                width: 40, height: 40, borderRadius: '50%',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                background: n.is_read ? 'var(--gray-100)' : 'var(--primary-light)',
                color: n.is_read ? 'var(--gray-500)' : getIconColor(n.title),
                fontSize: 18, flexShrink: 0,
              }}>
                {getIcon(n.title)}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                  <span style={{ fontWeight: n.is_read ? 500 : 700, fontSize: 14, color: 'var(--gray-900)' }}>
                    {n.title}
                  </span>
                  <span style={{ fontSize: 12, color: 'var(--gray-500)', whiteSpace: 'nowrap', marginLeft: 12 }}>
                    {formatDate(n.createdAt || n.created_at, { withTime: true })}
                  </span>
                </div>
                <p style={{ fontSize: 13, color: 'var(--gray-600)', lineHeight: 1.5, wordBreak: 'break-word' }}>
                  {n.message}
                </p>
              </div>
              {!n.is_read && (
                <div style={{
                  width: 10, height: 10, borderRadius: '50%',
                  background: 'var(--primary)', flexShrink: 0, marginTop: 6,
                }} />
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default NotificationsPage;
